import { useEffect, useRef, useState } from 'react'

import { useForm, useFormState, useWatch } from 'react-hook-form'
import { isAddress } from 'viem'

import { AddressInput, TextInput } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { Tabs, TabsContent } from '~/components/component-library/Tabs'
import { useRecoveryAddressValidation } from '~/components/create-workspace/steps/5_RecoveryStep/hooks/useRecoveryAddressValidation'
import { useSubmitRecoverySigner } from '~/components/create-workspace/steps/5_RecoveryStep/hooks/useSubmitRecoverySigner'
import { useActiveOrgSmartAccountsSetup } from '~/hooks/org'
import { useContactLookup } from '~/hooks/useContacts'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import { isValidEmail } from '~/utils/authUser'
import { emailsMatch } from '~/utils/email'

type Props = {
  onClose: () => void
  onSignerAdded: (newSignerCount: number) => Promise<void>
  address?: IAddress
}

export const RecoverySignerDialog = ({
  onClose,
  onSignerAdded,
  address: existingAddress,
}: Props) => {
  const isEdit = !!existingAddress

  const setup = useActiveOrgSmartAccountsSetup()
  const validateRecoveryAddress = useRecoveryAddressValidation(existingAddress)
  const { data: contact } = useContactLookup(existingAddress)

  const existingSigners = setup?.eoaSigners ?? []
  const existingSigner = isEdit
    ? existingSigners.find((s) => s.address === existingAddress)
    : undefined
  const wasEmail = !!existingSigner?.email

  const { onSubmit: submitRecoverySigner, isSubmitting } =
    useSubmitRecoverySigner({
      existingSigner,
      onSuccess: onClose,
      onSignerAdded,
    })

  // Controlled tabs — first usage in codebase (all others use uncontrolled defaultValue).
  // Controlled mode is needed to default the tab to the existing signer's type
  // and to trigger form reset as a side effect of tab change in create mode.
  const [activeTab, setActiveTab] = useState<'wallet' | 'email'>(
    isEdit ? (wasEmail ? 'email' : 'wallet') : 'email',
  )

  const {
    control,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    setFocus,
    trigger,
    reset,
  } = useForm<{
    address: string
    name: string
    email: string
  }>({
    defaultValues: {
      address: !wasEmail && existingAddress ? existingAddress : '',
      name: '',
      email: wasEmail ? (existingSigner?.email ?? '') : '',
    },
  })

  const address = useWatch({ control, name: 'address' })
  const name = useWatch({ control, name: 'name' })
  const email = useWatch({ control, name: 'email' })

  // AddressInput re-validates on every keystroke, so these track the live
  // result of the async EOA/smart-contract check (not just submit). We gate
  // Save on them so a well-formed-but-rejected address (e.g. a contract) keeps
  // the button disabled instead of allowing a dead click.
  const { errors, isValidating } = useFormState({ control })

  useEffect(() => {
    if (contact?.orgLabel) setValue('name', contact.orgLabel)
  }, [contact?.orgLabel, setValue])

  const emailTouchedRef = useRef(false)
  if (email) emailTouchedRef.current = true

  const isDuplicateEmail =
    isValidEmail(email) &&
    existingSigners.some(
      (s) => s.address !== existingAddress && emailsMatch(s.email, email),
    )

  useEffect(() => {
    if (isDuplicateEmail) {
      setError('email', { message: 'Email already used' })
    } else if (emailTouchedRef.current && !email) {
      setError('email', { message: 'Required' })
    } else {
      clearErrors('email')
    }
  }, [isDuplicateEmail, email, setError, clearErrors])

  // In create mode, switching tabs clears the form. In edit mode, we keep
  // values so switching back to the original tab restores prefilled state.
  const handleTabChange = (value: string) => {
    setActiveTab(value as 'wallet' | 'email')
    emailTouchedRef.current = false
    if (!isEdit) {
      reset({ address: '', name: '', email: '' })
    }
    // Radix switches tabs on mousedown, so the clicked tab takes focus after
    // the input mounts. Focus the input once the click settles.
    setTimeout(() => setFocus(value === 'wallet' ? 'address' : 'email'))
  }

  const trimmedName = name.trim()
  const hasNameChanged = trimmedName !== (contact?.orgLabel ?? '')

  const walletAddressDiffers =
    !existingSigner ||
    wasEmail ||
    !Address.equal(existingSigner.address, address)
  const isWalletTabValid =
    isAddress(address) &&
    !errors.address &&
    !isValidating &&
    (walletAddressDiffers || (!wasEmail && hasNameChanged))

  const emailDiffers =
    !existingSigner || !wasEmail || !emailsMatch(existingSigner.email, email)
  const isEmailTabValid =
    isValidEmail(email) &&
    !isDuplicateEmail &&
    (emailDiffers || (wasEmail && hasNameChanged))

  const isValid = activeTab === 'wallet' ? isWalletTabValid : isEmailTabValid

  return (
    <SplitsDialogContent
      id={isEdit ? 'editRecoverySignerDialog' : 'newRecoverySignerDialog'}
      title={isEdit ? 'Edit recovery signer' : 'Create recovery signer'}
      variant="cta"
      isLoading={isSubmitting}
      actionButton={{
        text: 'Save',
        onClick: handleSubmit((data) => submitRecoverySigner(activeTab, data)),
        isDisabled: !isValid,
      }}
    >
      <p>
        Add a trusted person who can recover your accounts in case you lose
        access to your passkeys.
      </p>
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        options={[
          { value: 'email', display: 'Email' },
          { value: 'wallet', display: 'Wallet' },
        ]}
      >
        <TabsContent value="wallet">
          <div className="space-y-4">
            <AddressInput
              label="Address or ENS"
              tooltip="Must be an externally owned account (EOA)"
              control={control}
              inputName="address"
              placeholder="0x6b48...E168 or minshew.eth"
              autoFocus={!isEdit || !wasEmail}
              setValue={setValue}
              setError={setError}
              clearErrors={clearErrors}
              trigger={trigger}
              validationFunc={validateRecoveryAddress}
              onENSValidityChange={({ valid, ens }) =>
                setValue('name', valid ? ens : '')
              }
              onValidAddress={() => setFocus('name')}
              addressPopoverEditDisabled
            />
            <TextInput
              label="Display name"
              control={control}
              inputName="name"
              placeholder="Will Minshew"
              optional
            />
          </div>
        </TabsContent>
        <TabsContent value="email">
          <div className="space-y-4">
            <TextInput
              label="Email"
              tooltip="Protected by Privy"
              control={control}
              inputName="email"
              placeholder="name@example.com"
              autoFocus={!isEdit || wasEmail}
              validationFunc={(value: string) => {
                if (!value) return true
                if (!isValidEmail(value)) return 'Invalid email address'
                if (
                  existingSigners.some(
                    (s) =>
                      s.address !== existingAddress &&
                      emailsMatch(s.email, value),
                  )
                )
                  return 'This email is already a recovery signer'
                return true
              }}
            />
            <TextInput
              label="Display name"
              control={control}
              inputName="name"
              placeholder="Will Minshew"
              optional
            />
          </div>
        </TabsContent>
      </Tabs>
    </SplitsDialogContent>
  )
}
